import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { PointFormMode } from '@shared/types/points';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class BusinessRouter {
  private readonly webPageTitleHandler = inject(Title);
  private readonly router = inject(Router);

  /**
   * Navigates to a specific meeting details page.
   *
   * @param meetingId - The unique identifier of the meeting to navigate to
   * @param options - Optional configuration object for navigation behavior
   * @param options.newTab - When true, opens the meeting page in a new browser tab; when false or undefined, navigates in the current tab
   */
  public goToAMeeting(meetingId: number, options?: { newTab: boolean }): void {
    if (options?.newTab) {
      window.open(`${environment.baseUrl}/private/meetings/details/${meetingId}`, '_blank');
    } else {
      this.router.navigateByUrl(`/private/meetings/details/${meetingId}`);
    }
  }

  /**
   * Navigates to a specific point details page.
   * @param pointId - The unique identifier of the point to navigate to
   * @param options.newTab - When true, opens the point page in a new browser tab; when false or undefined, navigates in the current tab
   */
  public goToAPoint(pointId: number, options?: { newTab: boolean }): void {
    if (options?.newTab) {
      window.open(`${environment.baseUrl}/private/points/detail/${pointId}`, '_blank');
    } else {
      this.router.navigateByUrl(`/private/points/detail/${pointId}`);
    }
  }

  /**
   * Navigates to a specific iCourrier mail details page.
   * It can only open through a new tab
   * @param mailId - The unique identifier of the mail to navigate to
   * @param options.newTab - When true, opens the mail page in a new browser tab; when false or undefined, navigates in the current tab
   */
  public goToAMail(mailId: number): void {
    window.open(`${environment.frontICourrierUrl}/private/ic/tabs/${mailId}`, '_blank');
  }

  public goToPointForm(options: {
    cancelRoute: string;
    mode: PointFormMode;
    pointId?: number;
    meetinId?: number;
  }): void {
    sessionStorage.setItem('point-form-cancel-route', options.cancelRoute);

    this.router.navigate([`/private/points/form`], {
      queryParams: { mode: options.mode, pointId: options.pointId, meetingId: options.meetinId },
    });
  }

  public goToReports(): void {
    this.router.navigateByUrl(`/private/reports`).then(() => {
      this.webPageTitleHandler.setTitle('Rapports');
    });
  }

  public goToPointsList(): void {
    this.router.navigateByUrl('/private/points/search/advancedSearch').then(() => {
      this.webPageTitleHandler.setTitle('Recherche avancée sur les points');
    });
  }

  public cancelPointCreationOrUpdate(): void {
    const cancelRoute = sessionStorage.getItem('point-form-cancel-route') ?? '/private/points';
    sessionStorage.removeItem('point-form-cancel-route');
    this.router.navigateByUrl(cancelRoute);
  }
}
